<template>
  <div id="calendar-widget" class="h-[218px] overflow-hidden relative w-[45vw]">
    <div
      id="calendar-widget__navigation--earlier"
      @click="previousMonth"
      class="calendar-widget__navigation"
      :class="[{ 'cursor-pointer': monthIndex > 0 }]"
    />

    <div
      id="calendar-widget__scroll-container"
      :style="{ transform }"
      class="transition-transform duration-300 ease-in-out"
    >
      <div
        v-for="(month, key) in months"
        :key="key"
        class="calendar-widget__month inline-block my-0 mx-[20px] font-medium"
      >
        <h1 class="text-base p-[10px] text-center">{{ month.title }}</h1>
        <table class="calendar-widget__month__table">
          <tr>
            <th
              v-for="weekDayName in ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo']"
              :key="weekDayName"
            >
              {{ weekDayName }}
            </th>
          </tr>
          <tr
            v-for="week in month.weeks"
            :key="week"
            class="calendar-widget__week"
          >
            <td
              v-for="day in week"
              :key="day.dayOfMonth"
              class="calendar-widget__day"
              :class="[
                {
                  'calendar-widget__day--foreign-month': day.isForeignMonth,
                  'calendar-widget__day--available': !!day.slug,
                  'calendar-widget__day--current': day.slug === routedDay.slug,
                },
              ]"
            >
              <div class="flex justify-center items-center w-full h-full">
                <router-link
                  v-if="day.slug"
                  :to="{
                    name: 'CalendarSheet',
                    params: {
                      ...$route.params,
                      daySlug: day.slug,
                    },
                  }"
                  class="calendar-widget__day__inner calendar-widget__day__inner--link block font-medium text-center no-underline"
                  >{{ day.dayOfMonth }}</router-link
                >
                <span
                  v-else
                  class="calendar-widget__day__inner calendar-widget__day__inner--no-link block font-medium text-center no-underline"
                  >{{ day.dayOfMonth }}</span
                >
              </div>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <div
      id="calendar-widget__navigation--later"
      @click="nextMonth"
      class="calendar-widget__navigation"
      :class="[
        { 'cursor-pointer': monthIndex < Object.keys(months).length - 1 },
      ]"
    />
  </div>
</template>

<script>
import { RouterLink } from "vue-router";

import { mapGetters } from "vuex";

import getWeekNumber from "../../../../store/getters/chronology/get-week-number";
import findDayByDate from "../../../../store/getters/resolve/find-day-by-date";
import store from "../../../../store";

export default {
  name: "CalendarWidget",
  components: {
    RouterLink,
  },
  data() {
    return {
      monthIndex: 0,
      tweenedTranslateX: 0,
    };
  },
  computed: {
    ...mapGetters([
      "activeSeason",
      "activeSeasonContentForSchoolLevel",
      "routedDay",
    ]),

    months() {
      /**
       * Generate the calendar widget days - month per month - for the season.
       * - Start month is month of season start date.
       * - End month is month of season end date.
       * - Query for days and add links.
       *
       *
       * Our algorithm for generating the calendar widget:
       *
       * - Go through months
       * - If day 1 of month is not a monday: reverse iterator until monday
       *     (backward into the previous month).
       * - Render the week into a container; (render days of foreign month disabled)
       * - Advance to the next day = next week
       * - Render the week into a container - repeat...
       * - If last day of month is not a sunday: iterate iterator until sunday
       *     (forward into the next month).
       * - Render the week into a container; (render days of foreign month disabled)
       * - Go to next month - repeat...
       */

      // Set month iterator to start at the first day of the season's start date's month.
      const monthPointer = new Date(this.activeSeason.startDate);
      this.setMidnight(monthPointer);
      // Set to first of the month.
      monthPointer.setDate(1);

      // Set end month to midnight of first day of month after season's last month.
      // We iterate the month pointer until < endMonth.
      const endMonth = new Date(this.activeSeason.endDate);
      this.setMidnight(endMonth);
      // Set to following month, first day.
      endMonth.setMonth(endMonth.getMonth() + 1);
      endMonth.setDate(1);

      const months = {};

      while (monthPointer < endMonth) {
        const monthKey = this.getMonthKey(monthPointer);

        // Prepare a month result object for the month at `monthPointer` if necessary.
        if (!Object.prototype.hasOwnProperty.call(months, monthKey)) {
          months[monthKey] = {
            title: monthPointer.toLocaleDateString("nl-NL", {
              month: "long",
              year: "numeric",
            }),
            weeks: {}, // As an object, using {Year}-{WeekNumber} as key.
          };
        }

        // Each month we start by copying the month pointer for a new day pointer,
        // and reversing it into the previous month, until we reach the Monday
        // of the current month's start week.
        const dayPointer = new Date(monthPointer.getTime());

        // Backtrack to Monday of the current week
        // (this intentionally backtracks into the previous month).
        // Note: While PHP's `DateTime::format('N')` counts from 1=Monday to 7=Sunday,
        // Javascript's `Date.prototype.getDay()` counts from 0=Sunday to 6=Saturday.
        // To make arithmetics easier, we map a Sunday `0` value back to `7`.
        const dayPointerDayOfWeek = dayPointer.getDay() || 7;
        if (dayPointerDayOfWeek !== 1) {
          dayPointer.setDate(dayPointer.getDate() - dayPointerDayOfWeek + 1);
        }

        // Now we "render" (in this case: collect data) the days in week-batches.

        // Break at the first Monday of the next month.
        // Note: Next month might lie in the following year.
        while (!(
          dayPointer.getDay() === 1 && // Monday
          this.getMonthKey(dayPointer) > monthKey // Next month
        )) {
          const weekNumber = this.getYearWeekNumber(dayPointer); // YYYY-MM
          if (
            !Object.prototype.hasOwnProperty.call(
              months[monthKey].weeks,
              weekNumber,
            )
          ) {
            months[monthKey].weeks[weekNumber] = [];
          }

          // Add a day to the week.
          months[monthKey].weeks[weekNumber].push({
            isForeignMonth: dayPointer.getMonth() !== monthPointer.getMonth(),
            dayOfMonth: dayPointer.getDate(),
            slug: findDayByDate(
              dayPointer,
              this.activeSeasonContentForSchoolLevel,
              store,
            )?.slug,
          });

          // `Date` knows to advance ot the next year or month when increasing
          // the date beyond the current month's bounds.
          // (32 August = 1 September.)
          dayPointer.setDate(dayPointer.getDate() + 1);
        }

        // `Date` knows to advance ot the next year when increasing
        // the month beyond the year's bounds.
        // (0 = January, 11 = December, 12 = January of the following year.)
        monthPointer.setMonth(monthPointer.getMonth() + 1);
      }

      return months;
    },

    translateX() {
      // Moving the widget horizontally.
      // We want to center the month of the `routedDay` by default.
      // Users can navigate the widget, selecting the next or previous month to be centered.
      // To avoid having to use `$refs`, we use %-based CSS `translateX()`,
      // rather than `px` values.
      const monthWidth = -100 / Object.keys(this.months).length;
      const base = monthWidth / 2;
      return base + this.monthIndex * monthWidth;
    },
    transform() {
      return `translateX(${this.translateX}%)`;
    },
  },
  watch: {
    routedDay() {
      // Center the day's month on day change.
      this.setMonthByDay(this.routedDay);
    },
  },
  mounted() {
    this.setMonthByDay(this.routedDay);
  },
  methods: {
    setMidnight(date) {
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
    },
    getYearWeekNumber(date) {
      // Format as PHP `W`.
      const [weekYear, weekNum] = getWeekNumber(date);
      return `${weekYear}-${String(weekNum).padStart(2, "0")}`;
    },
    getMonthKey(date) {
      // Format as PHP `Y-m`.
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}`;
    },
    previousMonth() {
      if (this.monthIndex === 0) {
        return;
      }
      this.monthIndex -= 1;
    },
    nextMonth() {
      if (this.monthIndex >= Object.keys(this.months).length - 1) {
        return;
      }
      this.monthIndex += 1;
    },
    setMonthByDay(day) {
      const monthKey = this.getMonthKey(new Date(day?.date));
      const monthIndex = Object.keys(this.months).indexOf(monthKey);
      this.monthIndex = monthIndex === -1 ? 0 : monthIndex;
    },
  },
};
</script>

<style lang="css">
.school-level-onderbouw .calendar-widget__month {
  & *,
  .calendar-widget__day__inner--link {
    @apply text-green;
  }

  .calendar-widget__day--current .calendar-widget__day__inner--link,
  .calendar-widget__day__inner--link:hover {
    @apply bg-green;
  }

  .calendar-widget__day__inner--no-link {
    @apply text-green opacity-60;
  }

  .calendar-widget__day--foreign-month {
    &:not(.calendar-widget__day--current) .calendar-widget__day__inner {
      @apply text-green opacity-30;
    }

    &.calendar-widget__day--current .calendar-widget__day__inner--link,
    .calendar-widget__day__inner--link:hover {
      @apply bg-green opacity-30;
    }
  }
}

/* OB repeated with MBB colors; otherwise identical. */
.school-level-midden-bovenbouw .calendar-widget__month {
  & *,
  .calendar-widget__day__inner--link {
    @apply text-purple;
  }

  .calendar-widget__day--current .calendar-widget__day__inner--link,
  .calendar-widget__day__inner--link:hover {
    @apply bg-purple;
  }

  .calendar-widget__day__inner--no-link {
    @apply text-purple opacity-60;
  }

  .calendar-widget__day--foreign-month {
    &:not(.calendar-widget__day--current) .calendar-widget__day__inner {
      @apply text-purple opacity-30;
    }

    &.calendar-widget__day--current .calendar-widget__day__inner--link,
    .calendar-widget__day__inner--link:hover {
      @apply bg-purple opacity-30;
    }
  }
}
</style>

<style scoped lang="css">
#calendar-widget {
  .calendar-widget__navigation {
    @apply box-border content-[''] block h-[218px] absolute w-[100px] z-10;

    &#calendar-widget__navigation--earlier {
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 1),
        rgba(255, 255, 255, 0.75) 50%,
        rgba(255, 255, 255, 0)
      );
      left: 0;
    }

    &#calendar-widget__navigation--later {
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 0),
        rgba(255, 255, 255, 0.75) 50%,
        rgba(255, 255, 255, 1)
      );
      right: 0;
    }
  }
}

#calendar-widget__scroll-container {
  display: flex;
  left: 50%;
  position: absolute;
  transform: translateX(0%);
}

.calendar-widget__month__table {
  border-spacing: 1px;
  border-collapse: separate;
}

.calendar-widget__day {
  & .calendar-widget__day__inner--link:hover,
  &.calendar-widget__day--current .calendar-widget__day__inner--link,
  &.calendar-widget__day--foreign-month
    .calendar-widget__day__inner--link:hover {
    @apply text-white;
  }
}

.calendar-widget__day__inner--link,
.calendar-widget__day__inner--no-link {
  @apply inline-flex items-center justify-center w-[26px] h-[26px] font-semibold leading-none p-0 text-center select-none;
}

.calendar-widget__day--current .calendar-widget__day__inner--link,
.calendar-widget__day__inner--link:hover {
  @apply rounded-full text-white;
  background-color: var(--your-hover-color);
}

.calendar-widget__day {
  @apply px-[7px] py-0;
}
</style>
